import { SPGameState } from "../enum/GameState";
import { SoundByte } from "../enum/sounds";
import MonoBehavior from "../lib/MonoBehavior";
import { Time } from "../lib/Time";
import Vector2 from "../math/Vector2";
import Listener from "../util/Listener";
import SoundManager from "./SoundManager";
import SPGameManager from "./SPGameManager";

export enum DialogueState {
    Closing,
    Closed,
    Opening,
    Open
}

type DialogueChoice = {
    text: string;
    callback: () => void;
}

type QueueItem = {
    text: string;
    time: number;
    name: string;
    choices?: DialogueChoice[];
}

export default class DialogueManager extends MonoBehavior {

    static Singleton: DialogueManager;

    Init(): void {
        if (DialogueManager.Singleton == undefined) {
            this.gameObject.layer = 2;
            DialogueManager.Singleton = this;
        }
        else if (!DialogueManager.Singleton.Equals(this)) this.gameObject.RemoveScript(this.id);
    }

    private queue: QueueItem[] = [];
    private currentQueueItem: QueueItem|undefined;

    state: DialogueState = DialogueState.Closed;
    windowSize: Vector2 = Vector2.zero;
    maxWindowSize: Vector2 = new Vector2(this.engine.width - 128, 128);
    windowCenter: Vector2 = new Vector2(this.engine.center.x, 96);
    currentText: string = "";
    textIndex = 0;
    lastTextIndex = 0;
    windowTimer = 0;
    listener: Listener = new Listener();
    name = '???';

    private choices: DialogueChoice[] = [];

    get hasChoices() {return this.choices.length > 0;}

    GetChoices() {
        return [...this.choices];
    }

    SetChoices(choices: DialogueChoice[]) {
        this.choices = choices;
        SPGameManager.Singleton.SetState(SPGameState.Pause);
    }

    ClearChoices() {
        this.choices = [];
    }

    ClearQueue() {
        this.queue = [];
    }

    ClearQueueAndClose() {
        this.queue = [];
        this.Close();
    }

    Queue(text: string, options: {
        stay?: number;
        name?: string;
        choices?: DialogueChoice[];
    } = {}) {
        this.queue.push({
            text,
            time: options.stay || 2,
            name: options.name || "",
            choices: options.choices
        });
    }

    Open(text: string, options: {
        stay?: number;
        name?: string;
    } = {}) {
        this.currentText = text;
        this.textIndex = 0;
        this.state = DialogueState.Opening;
        if (options.name) this.name = options.name;
        this.windowTimer = options.stay || 2;
    }

    Close() {
        this.currentText = '',
        this.textIndex = 0;
        this.state = DialogueState.Closing;
    }

    //#region state updates
    ClosedUpdate() {}

    ClosingUpdate() {
        this.windowSize = Vector2.lerp(this.windowSize, Vector2.zero, 8 * Time.deltaTime);

        if (Vector2.distance(this.windowSize, Vector2.zero) <= 1) {
            this.windowSize = Vector2.zero;
            this.state = DialogueState.Closed;
            this.listener.send("onClose", undefined);
        }
    }

    OpenUpdate() {

        if (this.textIndex < this.currentText.length) {
            this.textIndex += 40 * Time.deltaTime;
            if (Math.floor(this.textIndex) !== this.lastTextIndex) {
                this.lastTextIndex = Math.floor(this.textIndex);
                if (/[a-z\d]/i.test(this.currentText[this.lastTextIndex])) {
                    SoundManager.Singleton.PlaySound(SoundByte.TextBlip);
                }
            }
        } else {
            this.windowTimer -= Time.deltaTime;

            if (this.windowTimer <= 0) {
                this.windowTimer = 0;

                //has options?
                if (this.currentQueueItem?.choices) {
                    this.SetChoices(this.currentQueueItem.choices);
                    this.currentQueueItem = undefined;
                    return;
                }
                //

                if (this.queue.length > 0) {
                    const next = this.queue.shift();

                    if (next) {
                        this.currentQueueItem = next;
                        this.Open(next.text, {
                            stay: next.time,
                            name: next.name
                        });
                    }
                } else {
                    this.Close();
                }
                
            }
        }
    }

    OpeningUpdate() {
        this.windowSize = Vector2.lerp(this.windowSize, this.maxWindowSize, 8 * Time.deltaTime);

        if (Vector2.distance(this.windowSize, this.maxWindowSize) <= 1) {
            this.windowSize = this.maxWindowSize.clone();
            this.state = DialogueState.Open;
            this.listener.send("onOpen", undefined);
        }
    }
    //#endregion

    Update(): void {
        if (!SPGameManager.Singleton.isInGame) return;

        switch (this.state) {
            case DialogueState.Closed:
                this.ClosedUpdate();
                break;
            case DialogueState.Closing:
                this.ClosingUpdate();
                break;
            case DialogueState.Open:
                this.OpenUpdate();
                break;
            case DialogueState.Opening:
                this.OpeningUpdate();
                break;
        
            default:
                break;
        }

    }

    Draw(): void {

        if (this.state === DialogueState.Closed) return;

        const text = this.currentText.substring(0, Math.floor(this.textIndex));

        this.ctx.fillStyle = '#55ff55';
        this.engine.StrokeRectScreenFromCenter(this.windowCenter, this.windowSize);
        this.ctx.fillStyle = '#000000c0';
        this.engine.FillRectScreenFromCenter(this.windowCenter, this.windowSize.sub(Vector2.one.scale(4)));

        const topLeft = this.windowCenter.sub(this.windowSize.scale(0.5)).add(Vector2.one.scale(6));
        
        this.ctx.fillStyle = 'white';

        if (this.state === DialogueState.Open) {
            this.engine.drawTexts([this.name + ":"], topLeft.add(Vector2.up.scale(8)), {
                size: 12,
                baseline: "bottom"
            });
        }

        this.engine.drawTextWrap(text, topLeft, this.windowSize.x - 12);
    }

}